import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CLUSTER_TUNING_DEFAULTS,
  CLUSTER_TUNING_PARAMS,
  useClusterTuningStore,
  type ClusterTuning,
} from '../store/cluster-tuning-store';

const SLIDERS: {
  key: keyof ClusterTuning;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
}[] = [
  {
    key: 'mergePx',
    label: 'Merge distance',
    min: 0,
    max: 200,
    step: 5,
    unit: 'px',
  },
  {
    key: 'maxPx',
    label: 'Max bubble size',
    min: 28,
    max: 160,
    step: 2,
    unit: 'px',
  },
  {
    key: 'sizeExp',
    label: 'Size contrast',
    min: 0.15,
    max: 0.7,
    step: 0.05,
    unit: '',
  },
  {
    key: 'breakout',
    label: 'Pins at ≤',
    min: 0,
    max: 100,
    step: 5,
    unit: 'chargers',
  },
];

/** Preview-only: tune clustering live, then share a link */
export function ClusterTuningPanel() {
  const tuning = useClusterTuningStore();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  // Collapsed on phones, where it would cover the charger cards
  const [open, setOpen] = useState(() => window.innerWidth >= 800);

  const update = (patch: Partial<ClusterTuning>) => {
    tuning.set(patch);
    // The breakout decides what the API is asked for: refetch
    if ('breakout' in patch)
      void queryClient.invalidateQueries({ queryKey: ['charge-points'] });
  };

  const copyLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('cluster_tune', '1');
    for (const key of Object.keys(
      CLUSTER_TUNING_PARAMS
    ) as (keyof ClusterTuning)[])
      url.searchParams.set(CLUSTER_TUNING_PARAMS[key], String(tuning[key]));
    void navigator.clipboard.writeText(url.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="surface-float fixed bottom-4 mobile:bottom-auto mobile:top-44 left-1/2 z-30 w-72 -translate-x-1/2 rounded-2xl px-4 py-3 text-sm text-ink">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full justify-between font-semibold"
      >
        Cluster tuning {open ? '▾' : '▸'}
        <span className="font-normal text-muted tabular-nums">
          {tuning.stats.raw} → {tuning.stats.shown} clusters
        </span>
      </button>
      {open && (
        <div className="mt-3">
          {SLIDERS.map((s) => (
            <label key={s.key} className="mb-2 block">
              <span className="flex justify-between text-xs text-muted">
                {s.label}
                <span className="tabular-nums text-ink">
                  {tuning[s.key]} {s.unit}
                </span>
              </span>
              <input
                type="range"
                className="w-full accent-monta-blue"
                min={s.min}
                max={s.max}
                step={s.step}
                value={tuning[s.key]}
                onChange={(e) => update({ [s.key]: Number(e.target.value) })}
              />
            </label>
          ))}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={copyLink}
              className="h-9 flex-1 rounded-xl bg-monta-blue font-semibold text-white"
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={() => update(CLUSTER_TUNING_DEFAULTS)}
              className="h-9 rounded-xl bg-ink/5 px-3 ring-1 ring-inset ring-ink/10"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
